import { Flex, FlexProps } from 'component-library/components/layout/Flex';
import { ResponsiveBody } from 'component-library/components/typography/ResponsiveBody';
import { ResponsiveHeading } from 'component-library/components/typography/ResponsiveHeading';
import { Screen, ShowOn } from 'component-library/components/wrappers/ShowOn';
import { device } from 'component-library/styles/device';
import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';

export enum Alignment {
  Row = 'row',
  Column = 'column',
}

export interface FeatureSectionProps {
  heading: string;
  body: ReactNode;
  imageLarge: ReactNode;
  imageSmall: ReactNode;
  className?: string;
}

interface InternalFeatureSectionProps extends FeatureSectionProps, FlexProps {
  backgroundColor: string;
  alignment: Alignment;
}

const FeatureSectionWrapper = styled(Flex)<
  Pick<InternalFeatureSectionProps, 'backgroundColor'>
>`
  background-color: ${({ backgroundColor }) => backgroundColor};

  ${`@media ${device.lg}`} {
    padding: 64px 40px 40px;
    border-radius: 24px;
  }
`;

const TextWrapper = styled.div`
  flex-basis: 44%;
  padding: 40px 20px 0;

  ${`@media ${device.lg}`} {
    padding: 0;
  }
`;

const ImageWrapper = styled.div`
  flex-basis: 56%;

  ${`@media ${device.mdOnly}`} {
    max-width: 400px;
    margin: 0 auto;
  }
`;

const InnerWrapper = styled.div<Pick<InternalFeatureSectionProps, 'alignment'>>`
  display: ${({ alignment }) =>
    alignment === Alignment.Row ? 'flex' : 'block'};

  align-items: ${({ alignment }) =>
    alignment === Alignment.Row ? 'center' : 'unset'};

  ${`@media ${device.lg}`} {
    max-width: ${({ alignment }) =>
      `${alignment === Alignment.Column ? '575' : '1338'}px`};
  }
`;

export const FeatureSection: FC<InternalFeatureSectionProps> = ({
  heading,
  body,
  imageSmall,
  imageLarge,
  alignment,
  ...rest
}) => (
  <FeatureSectionWrapper justifyContent="center" {...rest}>
    <InnerWrapper alignment={alignment}>
      <TextWrapper>
        <ResponsiveHeading variant={4} marginBottom={16}>
          {heading}
        </ResponsiveHeading>
        <ResponsiveBody
          variant={2}
          marginBottom={alignment === Alignment.Column ? 32 : 0}
        >
          {body}
        </ResponsiveBody>
      </TextWrapper>
      <ImageWrapper>
        <ShowOn screens={[Screen.Sm, Screen.Md]}>{imageSmall}</ShowOn>
        <ShowOn screens={[Screen.Lg, Screen.Xl, Screen.Xxl]}>
          {imageLarge}
        </ShowOn>
      </ImageWrapper>
    </InnerWrapper>
  </FeatureSectionWrapper>
);
